/**
 * The repository of a SCOPE — "My boards & documents", or a team's (git sync
 * Stage 2, card S2.2; owner decision 02.10.2026: one repository per scope).
 * Layout (plan: Apiboo Documents › Git sync › "Git sync — Stage 2 plan"):
 *
 *   apiboo.yaml                                  scope manifest
 *   documents/<folder>/_folder.yaml              a document folder
 *   documents/<folder>/<title>-<id8>.md          a document (front matter + Markdown)
 *   documents/media/<id8>/<file>                 images / small videos (binary)
 *   boards/<name>-<id8>/board.yaml               a board: lists, labels, settings
 *   boards/<name>-<id8>/tasks/<title>-<id8>.md   a task (front matter + description + comments)
 *   projects/<name>-<id8>.yaml                   a project and its links
 *
 * Only these files are Apiboo's: everything else in the repository (README,
 * code, the user's own notes) is never written, deleted or read as data.
 */
import { BLOCK_BEGIN, BLOCK_END } from '../../utils/yamlWorkspace/schema.js';

export const SCOPE_ROOTS = ['documents', 'boards', 'projects'];
export const SCOPE_TOP_FILES = new Set(['apiboo.yaml', '.gitignore', '.gitattributes']);
export const MEDIA_DIR = 'documents/media';

const norm = (p) => String(p || '').replace(/\\/g, '/');
const topOf = (p) => norm(p).split('/')[0];
export const isMediaPath = (p) => norm(p).startsWith(`${MEDIA_DIR}/`);

/** A file Apiboo manages in a scope repository (writes, deletes as an orphan, reads as data). */
export function isScopeManaged(relPath) {
  const p = norm(relPath);
  if (!SCOPE_ROOTS.includes(topOf(p))) return false;
  if (isMediaPath(p)) return true;
  return (p.endsWith('.yaml') && !p.endsWith('.local.yaml')) || p.endsWith('.md');
}

/** A file the watcher applies when it changes outside Apiboo (text data — not media). */
export function isScopeDataPath(relPath) {
  const p = norm(relPath);
  if (p === 'apiboo.yaml') return true;
  return isScopeManaged(p) && !isMediaPath(p);
}

/** LF for text, and media never touched by line-ending conversion. */
export const SCOPE_GITATTRIBUTES = `${BLOCK_BEGIN}
*.yaml text eol=lf
*.md text eol=lf
documents/media/** binary
${BLOCK_END}
`;

export const SCOPE_GITIGNORE = `${BLOCK_BEGIN}
# Local-only files Apiboo may keep next to the data — never tracked.
*.local.yaml
${BLOCK_END}
`;

/* ── what changed, in Apiboo's words (Changes / History / commit messages) ─ */

export const SCOPE_GROUPS = ['Documents', 'Tasks', 'Boards', 'Projects', 'Media'];

/** The group of a scope-repository file from its path alone; null when it is not one. */
export function scopeGroupOfPath(relPath) {
  const p = norm(relPath);
  if (!isScopeManaged(p)) return null;
  if (isMediaPath(p)) return 'Media';
  if (topOf(p) === 'documents') return 'Documents';
  if (topOf(p) === 'projects') return 'Projects';
  if (/^boards\/[^/]+\/tasks\/[^/]+\.md$/.test(p)) return 'Tasks';
  if (topOf(p) === 'boards') return 'Boards';
  return null;
}

export const SCOPE_NOUNS = { Documents: 'document', Tasks: 'task', Boards: 'board', Projects: 'project', Media: 'image' };

/** `---` front matter of a .md file → { data: {key: value}, body }. Only `key: value` lines (what Apiboo writes). */
export function splitFrontMatter(text) {
  const s = String(text || '');
  const m = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/.exec(s);
  if (!m) return { data: {}, body: s };
  const data = {};
  for (const line of m[1].split(/\r?\n/)) {
    const kv = /^([A-Za-z_][\w-]*):\s?(.*)$/.exec(line);
    if (kv) data[kv[1]] = kv[2].replace(/^"(.*)"$/, '$1');
  }
  return { data, body: s.slice(m[0].length) };
}

/** A readable name for a scope file: the title / name it carries, else its file name. */
export function scopeItemName(relPath, text) {
  const p = norm(relPath);
  const file = p.split('/').pop();
  if (isMediaPath(p)) return file;
  if (p.endsWith('.md')) return splitFrontMatter(text).data.title || file.replace(/-[0-9a-f]{8}\.md$|\.md$/i, '');
  const m = /^name:\s*(.+)$/m.exec(String(text || ''));
  if (m) return m[1].trim().replace(/^["']|["']$/g, '');
  if (file === '_folder.yaml') return p.split('/').slice(-2, -1)[0];
  return file.replace(/\.yaml$/, '');
}

/**
 * What changed in one scope file, field by field where it can say: front
 * matter keys of a .md, and "Content" for its body.
 */
export function scopeFieldChanges(relPath, before, after) {
  if (!String(relPath).endsWith('.md') || before == null || after == null) return [];
  const a = splitFrontMatter(before);
  const b = splitFrontMatter(after);
  const fields = [];
  for (const key of [...new Set([...Object.keys(a.data), ...Object.keys(b.data)])].sort()) {
    if (key === 'id' || a.data[key] === b.data[key]) continue;
    const change = a.data[key] == null ? 'added' : b.data[key] == null ? 'removed' : 'changed';
    fields.push({ label: key.replace(/[_-]+/g, ' ').replace(/^\w/, (c) => c.toUpperCase()), change, before: a.data[key], after: b.data[key] });
  }
  if (a.body !== b.body) fields.push({ label: 'Content', change: 'changed', before: '…', after: '…' });
  return fields;
}
